import { type ReactNode, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Logo, cn } from "@zentrani/ui";
import { api } from "@zentrani/api-client";
import { useSession } from "../state/session";
import { BottomNav } from "./BottomNav";
import { NavIcon } from "./NavIcon";
import { allNavItems, navForRole } from "./navConfig";
import { PushOptInBanner } from "./PushOptInBanner";

function headerIconClass(isActive: boolean) {
  return cn(
    "relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-ink-soft",
    isActive ? "bg-brand-50 text-brand-800" : "hover:bg-slate-100"
  );
}

function UnreadChip({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute right-1 top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-stop px-1 text-[10px] font-semibold leading-none tabular-nums text-white ring-2 ring-white"
    >
      {count > 10 ? "+10" : count}
    </span>
  );
}

/** Shared shell — phone column by default; widens on tablet/desktop for all roles. */
export function AppShell({ role, children }: { role: string; children: ReactNode }) {
  const { session } = useSession();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const [unread, setUnread] = useState(0);
  const showNotifications = Boolean(session?.community_id);
  const nav = navForRole(role);
  const desktopNav = allNavItems(nav);
  const showBottomNav = nav.primary.length >= 2;

  useEffect(() => {
    if (!showNotifications) return;
    let cancelled = false;
    async function load() {
      try {
        const { count } = await api.notifications.unreadCount();
        if (!cancelled) setUnread(count);
      } catch {
        if (!cancelled) setUnread(0);
      }
    }
    void load();
    const onChanged = () => void load();
    window.addEventListener("zentrani:notifications-changed", onChanged);
    const id = window.setInterval(() => void load(), 60_000);
    return () => {
      cancelled = true;
      window.removeEventListener("zentrani:notifications-changed", onChanged);
      window.clearInterval(id);
    };
  }, [showNotifications, session?.user_id, session?.community_id]);

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!showBottomNav) return;
    const root = document.documentElement;
    const body = document.body;
    const mq = window.matchMedia("(max-width: 767px)");
    const previousRootOverflow = root.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousNavVar = root.style.getPropertyValue("--app-bottom-nav");

    function apply() {
      if (mq.matches) {
        root.style.overflow = "hidden";
        body.style.overflow = "hidden";
        root.style.setProperty("--app-bottom-nav", "6rem");
      } else {
        root.style.overflow = previousRootOverflow;
        body.style.overflow = previousBodyOverflow;
        root.style.setProperty("--app-bottom-nav", "0px");
      }
    }

    apply();
    mq.addEventListener("change", apply);
    return () => {
      mq.removeEventListener("change", apply);
      root.style.overflow = previousRootOverflow;
      body.style.overflow = previousBodyOverflow;
      if (previousNavVar) {
        root.style.setProperty("--app-bottom-nav", previousNavVar);
      } else {
        root.style.removeProperty("--app-bottom-nav");
      }
    };
  }, [showBottomNav]);

  return (
    <div
      className={cn(
        "relative bg-slate-50",
        showBottomNav
          ? "flex h-screen h-svh flex-col overflow-hidden md:h-auto md:min-h-dvh md:overflow-visible"
          : "min-h-dvh"
      )}
    >
      <header
        className={cn(
          "z-20 overflow-visible border-b border-slate-200 bg-white",
          showBottomNav ? "shrink-0 md:sticky md:top-0" : "sticky top-0"
        )}
      >
        <div className="mx-auto flex w-full max-w-app items-center justify-between gap-2 overflow-visible px-4 py-3 sm:gap-3 md:max-w-3xl lg:max-w-5xl">
          <Logo className="min-w-0 shrink" />
          <div className="ml-auto flex shrink-0 items-center gap-0.5">
            {showNotifications ? (
              <NavLink
                to="/notificaciones"
                aria-label={
                  unread > 0
                    ? `Notificaciones, ${unread > 10 ? "más de 10" : unread} sin leer`
                    : "Notificaciones"
                }
                title="Notificaciones"
                className={({ isActive }) => headerIconClass(isActive)}
              >
                <NavIcon name="notifications" />
                <UnreadChip count={unread} />
              </NavLink>
            ) : null}
            <NavLink
              to="/ajustes"
              aria-label="Ajustes"
              title="Ajustes"
              className={({ isActive }) => headerIconClass(isActive)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                width={24}
                height={24}
                className="h-6 w-6 shrink-0"
                aria-hidden="true"
              >
                <path
                  d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
              </svg>
            </NavLink>
          </div>
        </div>
        {desktopNav.length > 0 ? (
          <div className="hidden md:block">
            {/* Keep overflow off the flex row: Safari otherwise stacks tab glyphs. */}
            <div className="mx-auto w-full max-w-app overflow-x-auto overflow-y-hidden px-4 pb-2 md:max-w-3xl lg:max-w-5xl">
              <nav
                className="flex w-max min-w-full flex-nowrap gap-1 [font-variant-ligatures:none] [text-rendering:auto]"
                aria-label="Secciones"
              >
                {desktopNav.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        "inline-flex shrink-0 items-center whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium min-h-11 md:min-h-0",
                        isActive ? "bg-brand-50 text-brand-800" : "text-ink-soft hover:bg-slate-100"
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        ) : null}
      </header>
      <main
        ref={mainRef}
        className={cn(
          "mx-auto w-full max-w-app px-4 py-6 md:max-w-3xl lg:max-w-5xl lg:py-8",
          showBottomNav && "min-h-0 flex-1 overflow-y-auto overscroll-y-contain md:flex-none md:overflow-visible"
        )}
      >
        {role === "resident" ? <PushOptInBanner /> : null}
        {children}
      </main>
      {showBottomNav ? (
        <BottomNav primary={nav.primary} more={nav.more} scrollportRef={mainRef} />
      ) : null}
    </div>
  );
}
